/**
 * Submission Details Page - Microsoft 365 Style
 * Displays comprehensive onboarding submission information
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { makeStyles, Button, Text, Badge, Spinner } from '@fluentui/react-components';
import { ArrowLeftRegular, CheckmarkCircleRegular, DocumentRegular } from '@fluentui/react-icons';
import { ContentCard } from '../../../components/layout';
import { ApiClient, newToast } from '../../../configurations/index.config';
import { getErrorMessage } from '../../../types';

const useStyles = makeStyles({
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    marginBottom: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  backButton: {
    minWidth: 'auto',
  },
  pageTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
  },
  headerActions: {
    display: 'flex',
    gap: '8px',
  },
  submissionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    padding: '24px',
    borderBottom: '1px solid #e1dfdd',
  },
  submissionIcon: {
    width: '72px',
    height: '72px',
    borderRadius: '12px',
    backgroundColor: '#0078d4',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ffffff',
    fontSize: '32px',
  },
  clientName: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '4px',
  },
  submittedAt: {
    fontSize: '14px',
    color: '#605e5c',
  },
  contentGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '24px',
    padding: '24px',
  },
  section: {
    marginBottom: '0',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '16px',
    paddingBottom: '0.5px',
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  fieldLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#605e5c',
    textTransform: 'uppercase',
  },
  fieldValue: {
    fontSize: '14px',
    color: '#323130',
  },
  emptyState: {
    padding: '60px 20px',
    textAlign: 'center',
    color: '#605e5c',
  },
  fullWidthSection: {
    gridColumn: '1 / -1',
  },
  listItems: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  listItem: {
    backgroundColor: '#f3f2f1',
    padding: '4px 12px',
    borderRadius: '4px',
    fontSize: '13px',
  },
});

interface OnboardingSubmission {
  id: number;
  onboard_link_id: number;
  client_name: string;
  location_address: string;
  contact_number: string;
  email_address: string;
  postal_address?: string;
  services_required: string[];
  premises_types: string[];
  guards_day: number;
  guards_night: number;
  guards_total: number;
  date_commencement: string;
  duration_value: number;
  duration_unit: string;
  billing_name: string;
  billing_position?: string;
  billing_tel: string;
  billing_email: string;
  other_duties?: string[];
  status: 'pending' | 'approved' | 'rejected';
  submitted_at: string;
  created_at: string;
}

export const SubmissionDetailsFluentUI: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [submission, setSubmission] = useState<OnboardingSubmission | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchSubmissionDetails = useCallback(async () => {
    setLoading(true);
    try {
      const response = await ApiClient.get('/get-onboarding-submissions');
      if (response.data.status === 'success') {
        const submissions = response.data.data || [];
        const found = submissions.find((s: OnboardingSubmission) => s.id === Number(id));
        if (found) {
          setSubmission(found);
        } else {
          newToast('error', 'Submission not found');
          navigate('/account/onboard-client');
        }
      } else {
        newToast('error', response.data?.message || 'Failed to fetch submission details');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setLoading(false);
  }, [id, navigate]);

  useEffect(() => {
    if (id) {
      fetchSubmissionDetails();
    }
  }, [id, fetchSubmissionDetails]);

  const handleBack = () => {
    navigate('/account/onboard-client');
  };

  const handleApprove = async () => {
    if (!submission) {
      return;
    }
    try {
      await ApiClient.post('/approve-onboarding-submission', {
        id: submission.id,
      });
      newToast('success', 'Submission approved successfully');
      fetchSubmissionDetails();
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) {
      return '—';
    }
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDate = (dateString: string) => {
    if (!dateString) {
      return '—';
    }
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <Badge appearance="filled" color="warning">
            Pending
          </Badge>
        );
      case 'approved':
        return (
          <Badge appearance="filled" color="success">
            Approved
          </Badge>
        );
      case 'rejected':
        return (
          <Badge appearance="filled" color="danger">
            Rejected
          </Badge>
        );
      default:
        return (
          <Badge appearance="outline" color="informative">
            {status}
          </Badge>
        );
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <Spinner size="large" label="Loading submission details..." />
        </div>
      </div>
    );
  }

  if (!submission) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <Text size={500} weight="semibold">
            Submission not found
          </Text>
          <Button appearance="primary" onClick={handleBack} style={{ marginTop: '16px' }}>
            Back to Onboard Client
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Button
            icon={<ArrowLeftRegular />}
            appearance="subtle"
            onClick={handleBack}
            className={styles.backButton}
          />
          <Text className={styles.pageTitle}>Submission Details</Text>
        </div>
        <div className={styles.headerActions}>
          {submission.status === 'pending' && (
            <Button icon={<CheckmarkCircleRegular />} appearance="primary" onClick={handleApprove}>
              Approve
            </Button>
          )}
        </div>
      </div>

      {/* Submission Card */}
      <ContentCard>
        {/* Submission Header */}
        <div className={styles.submissionHeader}>
          <div className={styles.submissionIcon}>
            <DocumentRegular />
          </div>
          <div>
            <Text className={styles.clientName}>{submission.client_name}</Text>
            <Text className={styles.submittedAt}>
              Submitted {formatDateTime(submission.submitted_at)}
            </Text>
          </div>
          <div style={{ marginLeft: 'auto' }}>{getStatusBadge(submission.status)}</div>
        </div>

        {/* Submission Details Grid */}
        <div className={styles.contentGrid}>
          {/* Client Information */}
          <div className={styles.section}>
            <Text className={styles.sectionTitle}>Client Information</Text>
            <div className={styles.fieldGroup}>
              <div className={styles.field}>
                <Text className={styles.fieldLabel}>Company Name</Text>
                <Text className={styles.fieldValue}>{submission.client_name}</Text>
              </div>
              <div className={styles.field}>
                <Text className={styles.fieldLabel}>Location Address</Text>
                <Text className={styles.fieldValue}>{submission.location_address}</Text>
              </div>
              <div className={styles.field}>
                <Text className={styles.fieldLabel}>Postal Address</Text>
                <Text className={styles.fieldValue}>{submission.postal_address || '—'}</Text>
              </div>
              <div className={styles.field}>
                <Text className={styles.fieldLabel}>Email</Text>
                <Text className={styles.fieldValue}>{submission.email_address}</Text>
              </div>
              <div className={styles.field}>
                <Text className={styles.fieldLabel}>Contact Number</Text>
                <Text className={styles.fieldValue}>{submission.contact_number}</Text>
              </div>
            </div>
          </div>

          {/* Contract Details */}
          <div className={styles.section}>
            <Text className={styles.sectionTitle}>Contract Details</Text>
            <div className={styles.fieldGroup}>
              <div className={styles.field}>
                <Text className={styles.fieldLabel}>Date of Commencement</Text>
                <Text className={styles.fieldValue}>
                  {formatDate(submission.date_commencement)}
                </Text>
              </div>
              <div className={styles.field}>
                <Text className={styles.fieldLabel}>Duration</Text>
                <Text className={styles.fieldValue}>
                  {submission.duration_value} {submission.duration_unit}
                </Text>
              </div>
              <div className={styles.field}>
                <Text className={styles.fieldLabel}>Guards (Day / Night / Total)</Text>
                <Text className={styles.fieldValue}>
                  {submission.guards_day} / {submission.guards_night} / {submission.guards_total}
                </Text>
              </div>
            </div>
          </div>

          {/* Billing Contact */}
          <div className={styles.section}>
            <Text className={styles.sectionTitle}>Billing Contact</Text>
            <div className={styles.fieldGroup}>
              <div className={styles.field}>
                <Text className={styles.fieldLabel}>Name</Text>
                <Text className={styles.fieldValue}>{submission.billing_name}</Text>
              </div>
              <div className={styles.field}>
                <Text className={styles.fieldLabel}>Position</Text>
                <Text className={styles.fieldValue}>{submission.billing_position || '—'}</Text>
              </div>
              <div className={styles.field}>
                <Text className={styles.fieldLabel}>Phone</Text>
                <Text className={styles.fieldValue}>{submission.billing_tel}</Text>
              </div>
              <div className={styles.field}>
                <Text className={styles.fieldLabel}>Email</Text>
                <Text className={styles.fieldValue}>{submission.billing_email}</Text>
              </div>
            </div>
          </div>

          {/* Services Required */}
          <div className={`${styles.section} ${styles.fullWidthSection}`}>
            <Text className={styles.sectionTitle}>Services Required</Text>
            <div className={styles.listItems}>
              {submission.services_required && submission.services_required.length > 0 ? (
                submission.services_required.map((service, index) => (
                  <span key={index} className={styles.listItem}>
                    {service}
                  </span>
                ))
              ) : (
                <Text className={styles.fieldValue}>—</Text>
              )}
            </div>
          </div>

          {/* Premises Types */}
          <div className={`${styles.section} ${styles.fullWidthSection}`}>
            <Text className={styles.sectionTitle}>Premises Types</Text>
            <div className={styles.listItems}>
              {submission.premises_types && submission.premises_types.length > 0 ? (
                submission.premises_types.map((type, index) => (
                  <span key={index} className={styles.listItem}>
                    {type}
                  </span>
                ))
              ) : (
                <Text className={styles.fieldValue}>—</Text>
              )}
            </div>
          </div>

          {/* Other Duties */}
          {submission.other_duties && submission.other_duties.length > 0 && (
            <div className={`${styles.section} ${styles.fullWidthSection}`}>
              <Text className={styles.sectionTitle}>Other Guard Duties</Text>
              <div className={styles.fieldGroup}>
                {submission.other_duties.map((duty, index) => (
                  <Text key={index} className={styles.fieldValue}>
                    {index + 1}. {duty}
                  </Text>
                ))}
              </div>
            </div>
          )}
        </div>
      </ContentCard>
    </div>
  );
};

export default SubmissionDetailsFluentUI;
